import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getPlans, PlanConfig, PLAN_DEFAULTS } from '../../services/subscriptionService';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import './PlanUpgrade.css';

interface PlanUpgradeProps {
    onUpgradeClick?: (planKey: string) => void;
}

export const PlanUpgrade: React.FC<PlanUpgradeProps> = ({ onUpgradeClick }) => {
    const { user } = useAuth();
    const { subscription, isActive } = useSubscription();
    const [plans, setPlans] = useState<Record<string, PlanConfig>>(PLAN_DEFAULTS);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [showComparisonModal, setShowComparisonModal] = useState(false);
    const [proRataDiscount, setProRataDiscount] = useState<number>(0);

    useEffect(() => {
        getPlans().then(setPlans);
    }, []);

    useEffect(() => {
        calculateProRataDiscount();
    }, [subscription, selectedPlan, plans]);

    const calculateProRataDiscount = () => {
        if (!subscription || !selectedPlan || !subscription.subscriptionEndDate) {
            setProRataDiscount(0);
            return;
        }

        const currentPlanKey = subscription.planType || 'basic';
        const currentPlan = plans[currentPlanKey];
        const newPlan = plans[selectedPlan];

        if (!currentPlan || !newPlan) {
            setProRataDiscount(0);
            return;
        }

        // Calcular dias restantes
        const now = new Date();
        const endDate = new Date(subscription.subscriptionEndDate);
        const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        const totalDays = 30; // Assumindo ciclo mensal

        // Calcular valor proporcional do plano atual
        const currentPlanProRata = (currentPlan.price / totalDays) * daysRemaining;

        // Desconto é o valor proporcional do plano atual
        setProRataDiscount(currentPlanProRata);
    };

    const getCurrentPlanKey = () => {
        return subscription?.planType || null;
    };

    const getCurrentPlan = () => {
        const currentKey = getCurrentPlanKey();
        return currentKey ? plans[currentKey] : null;
    };

    const getUpgradeablePlans = () => {
        const currentKey = getCurrentPlanKey();
        if (!currentKey) return [];

        const planOrder = ['basic', 'medium', 'advanced'];
        const currentIndex = planOrder.indexOf(currentKey);

        if (currentIndex === -1) return planOrder;

        // Retorna apenas planos superiores
        return planOrder.slice(currentIndex + 1);
    };

    const getPlanFeatures = (planKey: string): string[] => {
        const plan = plans[planKey];
        const features = [
            `✓ Até ${plan.limit >= 9000 ? 'ilimitados' : plan.limit} pacientes`,
            '✓ Acesso completo à plataforma web',
            '✓ App mobile para pacientes',
            '✓ Gestão de consultas e agendamentos',
            '✓ Planos alimentares personalizados',
            '✓ Geração de planos com IA',
            '✓ Acompanhamento de progresso',
            '✓ Chat com pacientes',
            '✓ Videochamadas integradas',
        ];

        if (planKey === 'medium') {
            features.push('✓ Suporte prioritário');
            features.push('✓ Relatórios avançados');
        }

        if (planKey === 'advanced') {
            features.push('✓ Suporte VIP 24/7');
            features.push('✓ Relatórios avançados');
            features.push('✓ Personalização de marca');
            features.push('✓ API de integração');
            features.push('✓ Treinamento personalizado');
        }

        return features;
    };

    const getPlanDifferences = (fromPlan: string, toPlan: string): string[] => {
        const differences: string[] = [];
        const fromPlanConfig = plans[fromPlan];
        const toPlanConfig = plans[toPlan];

        // Diferença de limite de pacientes
        if (toPlanConfig.limit > fromPlanConfig.limit) {
            const limitDiff = toPlanConfig.limit >= 9000
                ? 'Pacientes ilimitados'
                : `+${toPlanConfig.limit - fromPlanConfig.limit} pacientes`;
            differences.push(`📈 ${limitDiff}`);
        }

        // Recursos específicos por plano
        if (toPlan === 'medium' && fromPlan === 'basic') {
            differences.push('⭐ Suporte prioritário');
            differences.push('📊 Relatórios avançados');
        }

        if (toPlan === 'advanced') {
            if (fromPlan === 'basic') {
                differences.push('👑 Suporte VIP 24/7');
                differences.push('📊 Relatórios avançados');
                differences.push('🎨 Personalização de marca');
                differences.push('🔌 API de integração');
                differences.push('🎓 Treinamento personalizado');
            } else if (fromPlan === 'medium') {
                differences.push('👑 Upgrade para Suporte VIP 24/7');
                differences.push('🎨 Personalização de marca');
                differences.push('🔌 API de integração');
                differences.push('🎓 Treinamento personalizado');
            }
        }

        return differences;
    };

    const handleUpgrade = (planKey: string) => {
        setSelectedPlan(planKey);
        setShowComparisonModal(true);
    };

    const confirmUpgrade = () => {
        if (selectedPlan && onUpgradeClick) {
            onUpgradeClick(selectedPlan);
        }
        setShowComparisonModal(false);
    };

    const currentPlan = getCurrentPlan();
    const currentPlanKey = getCurrentPlanKey();
    const upgradeablePlans = getUpgradeablePlans();

    if (!isActive || !currentPlan || !currentPlanKey) {
        return (
            <div className="plan-upgrade-container">
                <div className="plan-upgrade-empty">
                    <p>Você não possui uma assinatura ativa no momento.</p>
                    <p>Visite a página de <a href="/subscription">Assinatura</a> para escolher um plano.</p>
                </div>
            </div>
        );
    }

    if (upgradeablePlans.length === 0) {
        return (
            <div className="plan-upgrade-container">
                <div className="plan-upgrade-max">
                    <div className="plan-upgrade-max-icon">👑</div>
                    <h3>Você já está no melhor plano!</h3>
                    <p>Você possui o plano <strong>{currentPlan.name}</strong> com todos os recursos disponíveis.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="plan-upgrade-container">
            <div className="plan-upgrade-current">
                <div className="plan-upgrade-current-header">
                    <h3>Seu Plano Atual</h3>
                    <div className="plan-upgrade-current-badge">{currentPlan.name}</div>
                </div>
                <div className="plan-upgrade-current-info">
                    <div className="plan-upgrade-current-price">
                        R$ {currentPlan.price.toFixed(2).replace('.', ',')} <span>/mês</span>
                    </div>
                    <div className="plan-upgrade-current-limit">
                        {currentPlan.limit >= 9000 ? 'Pacientes ilimitados' : `Até ${currentPlan.limit} pacientes`}
                    </div>
                </div>
            </div>

            <div className="plan-upgrade-options">
                <h3>Faça Upgrade do Seu Plano</h3>
                <p className="plan-upgrade-subtitle">
                    Expanda sua capacidade e desbloqueie recursos exclusivos
                </p>

                <div className="plan-upgrade-grid">
                    {upgradeablePlans.map((planKey) => {
                        const plan = plans[planKey];
                        const differences = getPlanDifferences(currentPlanKey, planKey);
                        const priceDiff = plan.price - currentPlan.price;
                        const isPopular = planKey === 'medium';

                        return (
                            <div key={planKey} className={`plan-upgrade-card ${isPopular ? 'popular' : ''}`}>
                                {isPopular && <div className="plan-upgrade-popular-badge">Mais Popular</div>}

                                <div className="plan-upgrade-card-header">
                                    <h4>{plan.name}</h4>
                                    <div className="plan-upgrade-card-price">
                                        R$ {plan.price.toFixed(2).replace('.', ',')}
                                        <span>/mês</span>
                                    </div>
                                    <div className="plan-upgrade-card-diff">
                                        +R$ {priceDiff.toFixed(2).replace('.', ',')} /mês
                                    </div>
                                </div>

                                <div className="plan-upgrade-card-body">
                                    <div className="plan-upgrade-card-limit">
                                        {plan.limit >= 9000 ? '∞ Pacientes ilimitados' : `Até ${plan.limit} pacientes`}
                                    </div>

                                    <div className="plan-upgrade-card-differences">
                                        <strong>O que você ganha:</strong>
                                        <ul>
                                            {differences.map((diff, index) => (
                                                <li key={index}>{diff}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <div className="plan-upgrade-card-footer">
                                    <Button
                                        onClick={() => handleUpgrade(planKey)}
                                        style={{ width: '100%' }}
                                    >
                                        Fazer Upgrade
                                    </Button>
                                    <button
                                        className="plan-upgrade-compare-btn"
                                        onClick={() => {
                                            setSelectedPlan(planKey);
                                            setShowComparisonModal(true);
                                        }}
                                    >
                                        Ver comparação completa
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal de Comparação */}
            <Modal
                isOpen={showComparisonModal}
                onClose={() => setShowComparisonModal(false)}
                title="Comparação de Planos"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowComparisonModal(false)}
                        >
                            Cancelar
                        </Button>
                        <Button onClick={confirmUpgrade}>
                            Confirmar Upgrade
                        </Button>
                    </>
                }
            >
                {selectedPlan && (
                    <div className="plan-comparison-modal">
                        <div className="plan-comparison-grid">
                            <div className="plan-comparison-column">
                                <h4>Plano Atual</h4>
                                <div className="plan-comparison-name">{currentPlan.name}</div>
                                <div className="plan-comparison-price">
                                    R$ {currentPlan.price.toFixed(2).replace('.', ',')} /mês
                                </div>
                                <div className="plan-comparison-features">
                                    {getPlanFeatures(currentPlanKey).map((feature, index) => (
                                        <div key={index} className="plan-comparison-feature">
                                            {feature}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="plan-comparison-arrow">→</div>

                            <div className="plan-comparison-column highlight">
                                <h4>Novo Plano</h4>
                                <div className="plan-comparison-name">{plans[selectedPlan].name}</div>
                                <div className="plan-comparison-price">
                                    R$ {plans[selectedPlan].price.toFixed(2).replace('.', ',')} /mês
                                </div>
                                <div className="plan-comparison-features">
                                    {getPlanFeatures(selectedPlan).map((feature, index) => (
                                        <div key={index} className="plan-comparison-feature">
                                            {feature}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {proRataDiscount > 0 && subscription?.subscriptionEndDate && (
                            <div className="plan-comparison-discount">
                                <div className="plan-comparison-discount-icon">💰</div>
                                <div className="plan-comparison-discount-content">
                                    <strong>Desconto Proporcional Aplicado!</strong>
                                    <p>
                                        Você receberá um desconto de <strong>R$ {proRataDiscount.toFixed(2).replace('.', ',')}</strong> referente
                                        aos dias restantes do seu plano atual.
                                    </p>
                                    <div className="plan-comparison-discount-calc">
                                        <div>Valor do novo plano: R$ {plans[selectedPlan].price.toFixed(2).replace('.', ',')}</div>
                                        <div>Desconto proporcional: -R$ {proRataDiscount.toFixed(2).replace('.', ',')}</div>
                                        <div className="plan-comparison-discount-total">
                                            <strong>Valor a pagar hoje: R$ {Math.max(0, plans[selectedPlan].price - proRataDiscount).toFixed(2).replace('.', ',')}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="plan-comparison-info">
                            <p>
                                ℹ️ Ao fazer o upgrade, você terá acesso imediato a todos os recursos do novo plano.
                                O valor será ajustado proporcionalmente ao tempo restante do seu ciclo atual.
                            </p>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};
